<template>
  <div class="input-date-dmy">
    <!--
      Texto dd/mm/aaaa propio: el <input type="date"> nativo muestra el formato del
      idioma del navegador, y con el navegador en inglés el 01/11 se leía como 11 de enero.
      El valor que entra y sale sigue siendo YYYY-MM-DD.
    -->
    <div class="input-date-dmy__box">
      <input
        ref="text"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        maxlength="10"
        :id="inputId"
        :value="text"
        :placeholder="$t('dateInput.placeholder')"
        class="form-control"
        :class="[inputClass, { 'is-invalid': invalid }]"
        style="background: none"
        @input="onTextInput($event.target)"
        @change="commitText"
        @keydown.enter.prevent="commitText"
        @blur="onBlur"
        @focus="$emit('focus')"
      />
      <button
        v-if="!hidePicker"
        type="button"
        class="btn btn-sm border-0 input-date-dmy__picker"
        tabindex="-1"
        :title="$t('dateInput.openCalendar')"
        @click="openPicker"
      >
        <i class="material-icons">calendar_today</i>
      </button>
    </div>
    <!-- Solo para el calendario: elegir un día con el mouse no es ambiguo en ningún idioma. -->
    <input
      ref="native"
      type="date"
      class="input-date-dmy__native"
      tabindex="-1"
      aria-hidden="true"
      :value="isoValue"
      @change="onPickerChange($event.target.value)"
    />
    <small v-if="invalid" class="text-danger input-date-dmy__error">
      {{ $t("dateInput.invalid") }}
    </small>
  </div>
</template>

<script>
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** YYYY-MM-DD (o Date / ISO con hora) → YYYY-MM-DD, '' si no se puede leer. */
export function toIsoDate(value) {
  if (!value) return "";
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "";
    return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(
      value.getDate()
    )}`;
  }
  const m = String(value).match(ISO_RE);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
}

/** YYYY-MM-DD → dd/mm/aaaa */
export function isoToDmy(iso) {
  const m = String(iso || "").match(ISO_RE);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * dd/mm/aaaa (también d/m/aa, con - o .) → YYYY-MM-DD.
 * null si no es una fecha real (31/11, 29/02 en año no bisiesto, etc.).
 */
export function dmyToIso(text) {
  const m = String(text || "")
    .trim()
    .match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  let year = parseInt(m[3], 10);
  if (m[3].length === 2) year += 2000;
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** Inserta las barras mientras se tipean solo dígitos: 01112026 → 01/11/2026. */
function maskDigits(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export default {
  name: "InputDateDmy",
  props: {
    /** YYYY-MM-DD (se aceptan Date e ISO con hora por datos viejos). */
    value: {
      type: [String, Date],
      default: null,
    },
    inputId: {
      type: String,
      default: null,
    },
    inputClass: {
      type: [String, Object, Array],
      default: null,
    },
    hidePicker: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: isoToDmy(toIsoDate(this.value)),
      invalid: false,
    };
  },
  computed: {
    isoValue() {
      return toIsoDate(this.value);
    },
  },
  watch: {
    isoValue(iso) {
      // Un cambio que viene de afuera (otro campo, recarga) pisa lo tipeado,
      // salvo que sea la misma fecha escrita de otra forma.
      if (dmyToIso(this.text) !== iso) {
        this.text = isoToDmy(iso);
        this.invalid = false;
      }
    },
  },
  methods: {
    onTextInput(el) {
      const raw = el.value;
      // Solo se enmascara mientras se escribe al final y sin separadores propios
      // (borrar o editar en el medio no debe mover el cursor).
      const atEnd = el.selectionStart === raw.length;
      const next = atEnd && /^\d[\d/]*$/.test(raw) ? maskDigits(raw) : raw;
      this.text = next;
      if (next !== raw) el.value = next;
      this.invalid = false;
    },
    commitText() {
      const trimmed = this.text.trim();
      if (!trimmed) {
        this.invalid = false;
        if (this.isoValue) this.$emit("input", "");
        return;
      }
      const iso = dmyToIso(trimmed);
      if (!iso) {
        this.invalid = true;
        return;
      }
      this.invalid = false;
      this.text = isoToDmy(iso);
      if (iso !== this.isoValue) this.$emit("input", iso);
    },
    onBlur() {
      this.commitText();
      this.$emit("blur");
    },
    onPickerChange(iso) {
      const clean = toIsoDate(iso);
      this.text = isoToDmy(clean);
      this.invalid = false;
      this.$emit("input", clean);
      // El form propaga al salir del campo: el calendario no pasa por el blur del texto.
      this.$emit("blur");
    },
    openPicker() {
      const native = this.$refs.native;
      if (!native) return;
      if (typeof native.showPicker === "function") {
        try {
          native.showPicker();
          return;
        } catch (e) {
          // Navegadores sin showPicker o sin gesto de usuario: cae al focus.
        }
      }
      native.focus();
      native.click();
    },
  },
};
</script>

<style scoped>
.input-date-dmy {
  position: relative;
  width: 100%;
}

.input-date-dmy .form-control {
  padding-right: 34px !important;
}

/* Gana a los estilos de campo bloqueado/readonly del padre: una fecha rechazada tiene que verse. */
.input-date-dmy .form-control.is-invalid {
  border-color: #dc3545 !important;
}

.input-date-dmy__box {
  position: relative;
}

.input-date-dmy__error {
  display: block;
}

.input-date-dmy__picker {
  position: absolute;
  top: 50%;
  right: 2px;
  transform: translateY(-50%);
  padding: 2px 4px;
  line-height: 1;
  color: #6c757d;
  background: transparent;
}

.input-date-dmy__picker .material-icons {
  font-size: 18px;
}

/* Invisible pero presente, para que showPicker() ubique el calendario junto al campo. */
.input-date-dmy__native {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  border: 0;
  padding: 0;
}
</style>
